// @ts-nocheck
import { capitalize } from 'lodash';
import React from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { ButtonUI, calculateOptions } from 'sonata-react-components';
import { normalizeWithUnit } from 'sonata-static-storage/lib/helpers/i18nUnits.helpers';
import { useTheme } from 'styled-components';

import {
  getCartographyFilter,
  getMetricsKeys,
  getVariableFilterParsed,
  STORE_VISITS
} from '@/helpers/reporting.helpers';
import {
  getCartographyMapConfig,
  getCountriesWithEvents,
  getInsightsConfig,
  getInsightsUserConfig,
  getMetricsAndKpisByType,
  getPdfReportStatus,
  reportingProgressGetMetricAndKpi
} from '@/store/selectors/reporting.selectors';
import {
  getAdvancedFilters,
  getCampaigns,
  getCartographyCountries,
  getCartographyLevel,
  getCartographyLevelIds,
  getChartTimeStep,
  getTimeFrame,
  getTimeZone,
  getVariableType,
  getVariableValues
} from '@/store/selectors/reportingAdvancedFilter.selectors';
import { getBaseCurrencyCatalog } from '@/store/selectors/resources.selectors';
import { getCurrentUser } from '@/store/selectors/user.selectors';
import { useCampaignPerformance } from '@/ts-contexts/reporting/Summary/useCampaignPerformance';
import { useCampaignSummary } from '@/ts-contexts/reporting/Summary/useCampaignSummary';
import { getTextWidthAndHeight } from '@/ts-helpers/pdfTemplates.helpers';
import { useReportingEventsChart } from '@/ts-modules/reporting/Progress/ImpressionsKpisChart/ReportingEventsChart';
import { useCampaignsReportingLogo } from '@/ts-modules/reporting/reporting.hooks';
import { ReportingTemplateRequest } from '@/ts-resources/templates/requests/ReportingTemplate.request';
import { InsightItem, useReportPDFDownloadRequest } from '../../ReportDownload/hooks/usePDFReportDownloadRequest';

import type { ButtonUIProps } from 'sonata-react-components/lib/types/src_ts/components/ButtonUI/ButtonUI.types';
import type { CampaignItem } from '@/ts-modules/reporting/CampaignListSelector/CampaignListSelector';

type Props = {
  buttonProps: ButtonUIProps;
  id: string;
  omitAIInsights?: boolean;
  selectedCampaigns: CampaignItem[];
};

function DownloadPDFButtonV2({ buttonProps, id, omitAIInsights, selectedCampaigns }: Readonly<Props>) {
  const { uiColors }: any = useTheme();
  const { formatMessage } = useIntl();
  const user = useSelector(getCurrentUser);
  const selectedCampaignsIds = selectedCampaigns.map((campaign: any) => campaign.id);
  const timeFrame = useSelector(getTimeFrame);
  const timezone = useSelector(getTimeZone);
  const countries: string[] = useSelector(getCountriesWithEvents);
  const metricsAndKpi = useSelector(getMetricsAndKpisByType);
  const { config, filters } = useSelector(getInsightsConfig);
  const generalVariableType: string = useSelector(store => getVariableType(store, 'general'));
  const generalVariableValues: string[] = useSelector(store => getVariableValues(store, 'general'));
  const generalCartographyCountries = useSelector(store => getCartographyCountries(store, 'general'));
  const generalCartographyLevel = useSelector(store => getCartographyLevel(store, 'general'));
  const generalCartographyLevelIds = useSelector(store => getCartographyLevelIds(store, 'general'));
  const pdfReportStore = useSelector(store => getPdfReportStatus(store, id));
  const variableFilter = generalVariableValues.length
    ? getVariableFilterParsed(generalVariableType, generalVariableValues)
    : {};
  const cartographyMapConfig: {
    countries?: string[];
    filter?: {
      countries: string[];
      level: string;
      shapeIds: string[];
    };
    level?: string;
    metric?: string;
  } = useSelector(getCartographyMapConfig);

  const cartographyFilter = getCartographyFilter({
    cartographyCountries: generalCartographyCountries,
    cartographyLevel: generalCartographyLevel,
    cartographyLevelIds: generalCartographyLevelIds
  });

  const defaultMetric =
    cartographyMapConfig.metric ||
    metricsAndKpi.filter(item => !STORE_VISITS.includes(item)).map(item => getMetricsKeys(item))[0];

  const {
    cartographyLevel,
    cartographyLevelIds,
    cartographyLevelIdsNames,
    currency,
    timeRange,
    variableType,
    variableValuesNames
  } = useSelector(getAdvancedFilters);

  const { kpi, metric } = useSelector(store => reportingProgressGetMetricAndKpi(store));
  const reportingCampaignsIds = useSelector(getCampaigns);
  const timeStep = useSelector(getChartTimeStep);
  const currentInsightsConfig = useSelector(getInsightsUserConfig);

  const { chartConfig: calendar, isReady: isCalendarReady } = useReportingEventsChart({
    cids: reportingCampaignsIds,
    country: user.country,
    disaggregated: 'none',
    disaggregatedBy: undefined,
    groupBy: timeStep,
    hideKpis: !kpi,
    metricsKpis: {
      kpi,
      metric
    },
    showAllLegend: false,
    timezone
  });

  const { chartConfig: daily, isReady: isDailyReady } = useReportingEventsChart({
    cids: reportingCampaignsIds,
    country: user.country,
    disaggregated: 'none',
    disaggregatedBy: undefined,
    groupBy: 'dow',
    hideKpis: !kpi,
    metricsKpis: {
      kpi,
      metric
    },
    showAllLegend: false,
    timezone
  });

  const { chartConfig: hourly, isReady: isHourlyReady } = useReportingEventsChart({
    cids: reportingCampaignsIds,
    country: user.country,
    disaggregated: 'none',
    disaggregatedBy: undefined,
    groupBy: 'hod',
    hideKpis: !kpi,
    metricsKpis: {
      kpi,
      metric
    },
    showAllLegend: false,
    timezone
  });

  const baseCurrencyCatalog = useSelector(getBaseCurrencyCatalog);
  const currencyInfo = baseCurrencyCatalog.find((c: { baseCurrency: string }) => c.baseCurrency === currency);

  const performance = useCampaignPerformance({ filterSection: 'summary' });
  const summary = useCampaignSummary({
    filterSection: 'summary',
    showAnalyticalView: true
  });

  const isSummaryReady = performance.isReady && summary.isReady;

  const isReportingProgressReady = isCalendarReady && isDailyReady && isHourlyReady;

  const campaignNames = selectedCampaigns.map(item => item.name);

  const requestInit = new ReportingTemplateRequest(formatMessage, currencyInfo, campaignNames);

  const isDisabled = selectedCampaigns && selectedCampaigns.length === 0;

  const { logo } = useCampaignsReportingLogo(user.id, selectedCampaignsIds);

  const isReportingReadyToGenerate = !isDisabled && isSummaryReady && isReportingProgressReady;

  const mapCountries = cartographyMapConfig.filter?.countries.length
    ? cartographyMapConfig.filter?.countries
    : cartographyMapConfig?.countries?.length
      ? cartographyMapConfig?.countries
      : countries;

  const pdfCartographyMapConfig = {
    opts: {
      currencyOptions: {
        currency
      }
    },
    params: {
      dateInterval: {
        ...timeFrame,
        timezone
      },
      filters: {
        cartography: {
          level0: mapCountries,
          ...(cartographyMapConfig?.filter?.level && {
            [cartographyMapConfig?.filter?.level]: cartographyMapConfig?.filter?.shapeIds
          })
        }
      },
      level: mapCountries.length > 1 ? 'level0' : cartographyMapConfig.level,
      metric: defaultMetric,
      metricLabel: formatMessage({
        defaultMessage: capitalize(defaultMetric),
        id: `reporting.metrics.${defaultMetric}`
      })
    }
  };

  const formatInsightItemConfig = (baseConfig: any): InsightItem => {
    const [, currentItemGroupByConfig] =
      Object.entries(currentInsightsConfig.groupByConfig || {}).find(([key]) => key.split('_')[1] === baseConfig.id) ||
      ([] as any[]);
    const selectedGroupByOption =
      currentItemGroupByConfig?.groupBy?.id && baseConfig.id === 'cartography'
        ? `cartoPyramid.${currentItemGroupByConfig?.groupBy?.id}`
        : currentItemGroupByConfig?.groupBy?.id;

    return {
      chartType: baseConfig.chartType,
      colorAxis: baseConfig.chartConfig?.colorAxis,
      colorByLabel: baseConfig.chartConfig?.colorByLabel,
      colors: baseConfig.rankingConfig?.colors?.items,
      ...(currentItemGroupByConfig && {
        extraInfo: `${currentItemGroupByConfig?.groupBy?.label}${
          currentItemGroupByConfig?.groupBy?.country ? ` ${currentItemGroupByConfig?.groupBy?.country}` : ''
        }`
      }),
      filterOptions: baseConfig.filterOptions,
      groupsOptions: baseConfig.groupsOptions.map((gOpt: Record<string, any>) => ({
        ...gOpt,
        field: selectedGroupByOption || gOpt.groupBy || baseConfig.field,
        size: 10
      })),
      id: baseConfig.id,
      permissions: baseConfig.permissions,
      showProgressBar: baseConfig.rankingConfig?.showProgressBar || false,
      title: formatMessage({ id: baseConfig.title }),
      variant: baseConfig.variant
    };
  };

  const insightsConfig = {
    families: config
      .filter(c => c.items.length)
      .map(c => ({
        ...c,
        items: c.items.map(formatInsightItemConfig),
        label: formatMessage({
          defaultMessage: capitalize(c.type),
          id: filters.find(f => f.value === c.type)?.label
        })
      })),
    general: {
      metricsConfig: currentInsightsConfig.metricsConfig,
      opts: {
        currencyOptions: {
          currency
        }
      },
      params: {
        cartography: cartographyFilter,
        dateInterval: {
          ...timeFrame,
          timezone
        },
        filters: variableFilter
      }
    }
  };

  const getSummaryNormalized = () => {
    return summary?.isReady
      ? {
          isReady: true,
          items: summary?.items?.map((item: any) => {
            const { key, middle, top } = item;

            return {
              bottomMetricLabels: middle.map((m: any) => {
                const { numberLabelConfig, stringValue, text } = m;
                let value = stringValue;

                if (numberLabelConfig) {
                  const parsedAmount = m.na
                    ? 'N/A'
                    : normalizeWithUnit(
                        numberLabelConfig.amount,
                        {
                          currencyPosition: numberLabelConfig.symbolPosition,
                          currencySymbol: numberLabelConfig.symbol
                        },
                        numberLabelConfig.decimals
                      );
                  value = parsedAmount;
                }

                return {
                  text,
                  value
                };
              }),
              color: top.progressBarColor,
              goal: top.displayGoalMax,
              goalCurrent: top.number,
              goalTarget: top.goalMax,
              graph: {
                config: calculateOptions(uiColors, {
                  ...top.chartSeries,
                  height: 30
                }),
                graphId: `graph_${key}`
              },
              icon: top.icon,
              key,
              text: top.text,
              trendDown: !top.trendState || top.trendState === 'down',
              trendUp: top.trendState === 'up',
              value: top.displayNumber
            };
          })
        }
      : {
          isReady: false,
          items: []
        };
  };

  const summaryConfig = {
    performance,
    summaryNormalized: getSummaryNormalized()
  };

  const getTotalTagsHeight = () => {
    const containerWidth = 1650;
    return campaignNames.reduce((acc, name) => {
      const { height } = getTextWidthAndHeight(name, containerWidth, 16, 8);
      return acc + height;
    }, 0);
  };

  const campaignProgressConfig = {
    campaignNames,
    dailyGraphConfig: isDailyReady && calculateOptions(uiColors, daily),
    graphConfig: isCalendarReady && calculateOptions(uiColors, calendar),
    hourlyGraphConfig: isHourlyReady && calculateOptions(uiColors, hourly),
    kpi,
    metric,
    totalTagsHeight: getTotalTagsHeight()
  };

  const { generateReport, isLoading } = useReportPDFDownloadRequest({
    campaignProgressConfig,
    campaignsIds: selectedCampaignsIds,
    cartographyMapConfig: pdfCartographyMapConfig,
    country: currencyInfo?.countries?.[0],
    filters: {
      calendar,
      cartographyFilters: cartographyLevelIdsNames,
      cartographyLevel,
      cartographyLevelIds,
      currencyInfo,
      daily,
      hourly,
      kpi,
      logo,
      metric,
      timeFrame,
      timeRange,
      timezone,
      variableType,
      variableValuesNames
    },
    id,
    insightsConfig,
    isEnabled: isReportingReadyToGenerate,
    omits: {
      aiInsights: omitAIInsights
    },
    requestInit,
    summaryConfig,
    userId: user.id
  });

  return (
    <ButtonUI
      {...buttonProps}
      disabled={!isReportingReadyToGenerate}
      loading={isLoading || pdfReportStore?.status === 'pending'}
      onClick={generateReport}
    />
  );
}

export default DownloadPDFButtonV2;
